import React, { useState, useMemo } from 'react';
import { Card, Table, Container, Row, Col, Form, OverlayTrigger, Tooltip, Button, InputGroup, Overlay } from 'react-bootstrap';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import { GanttResourceRow } from '../../../models/GanttChartPlan';
import { CareerLevelRatesRestDataProvider, CareerLevelRate } from '../../../data/CareerLevelRatesRestDataProvider';
import PopupDialog from '../../common/PopupDialog/PopupDialog';

function getMonday(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function addWeeks(date: Date, weeks: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
}

function formatDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function utcIsoToLocalDateString(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  // Get local date parts
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function localDateStringToUtcIso(dateString: string): string {
  // Treat as local date, create Date at midnight local time, then get UTC ISO string
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toISOString();
}

function generateGuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const WEEK_COL_WIDTH = 72; // px, adjust as needed
const FIRST_COL_WIDTH = 360; // px
const FTE_COL_WIDTH = 56; // px, fixed width for FTE column
const RATE_COL_WIDTH = 160; // px, fixed width for cost/commercial rate columns

const HEADER_BG = '#f4f6fa';
const PHASE_BG = '#f1f5f9';
const RESOURCE_BG = '#fff';
const ADDROW_BG = '#f8fafc';
const BORDER_COLOR = '#e5e7eb';
const ALLOC_COLOR = '#6366f1';

interface GanttChartPlanProps {
  scenarioId: string;
  onDirtyChange?: (dirty: boolean) => void;
}

const GanttChartPlan: React.FC<GanttChartPlanProps> = ({ scenarioId, onDirtyChange }) => {
  const { scenarios, setScenarios } = useProjectScenarios();
  const scenario = scenarios.find(s => s.id === scenarioId);
  const plan = scenario?.ganttChartPlan;

  // Controlled from/to dates
  const initialFrom = plan?.fromDate ? utcIsoToLocalDateString(plan?.fromDate) : formatDate(getMonday(new Date()));
  const initialTo = plan?.toDate ? utcIsoToLocalDateString(plan?.toDate) : formatDate(addWeeks(getMonday(new Date()), 9));
  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);
  const [newSubrowName, setNewSubrowName] = useState<{ [phaseId: string]: string }>({});

  // Career level rates popup state
  const [ratePopover, setRatePopover] = useState<{ show: boolean; target: HTMLElement | null; field: 'cost' | 'commercial' | null; resourceId: string | null }>({ show: false, target: null, field: null, resourceId: null });
  const [careerLevelRates, setCareerLevelRates] = useState<CareerLevelRate[]>([]);
  const rateProvider = useMemo(() => new CareerLevelRatesRestDataProvider(), []);

  // State for remove confirmation dialog
  const [removeDialog, setRemoveDialog] = useState<{
    open: boolean;
    phaseId?: string;
    resourceId?: string;
    resourceName?: string;
  }>({ open: false });

  // State for editing resource name
  const [editingResource, setEditingResource] = useState<{ phaseId?: string; resourceId?: string; name?: string }>({});

  // Fetch rates on first open
  // Accept both mouse and focus events for accessibility
  const handleShowRates = async (
    e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
    field: 'cost' | 'commercial',
    resourceId: string
  ) => {
    setRatePopover({ show: true, target: e.currentTarget, field, resourceId });
    if (careerLevelRates.length === 0) {
      const rates = await rateProvider.getAll();
      setCareerLevelRates(rates);
    }
  };
  const handleHideRates = () => setRatePopover({ show: false, target: null, field: null, resourceId: null });

  // Calculate week columns based on from/to
  const weekMondays = useMemo(() => {
    const start = getMonday(new Date(fromDate));
    const end = getMonday(new Date(toDate));
    const mondays = [];
    let d = new Date(start);
    while (d <= end) {
      mondays.push(new Date(d));
      d = addWeeks(d, 1);
    }
    return mondays;
  }, [fromDate, toDate]);

  // Add a new subrow (resource) to a phase
  const handleAddSubrow = (phaseId: string) => {
    const name = (newSubrowName[phaseId] || '').trim();
    if (!name || !plan) return;
    const newResource: GanttResourceRow = {
      id: generateGuid(),
      name,
      fte: 1,
      costRate: 0,
      commercialRate: 0,
      allocations: []
    };
    // Update scenario in context
    const updatedPlan = {
      ...plan,
      phases: plan.phases.map(phase =>
        phase.id === phaseId
          ? { ...phase, resources: [...phase.resources, newResource] }
          : phase
      )
    };
    if (!scenario) return;
    const currentScenarioId = scenario.id;
    setScenarios(scenarios.map(s =>
      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
    ));
    setNewSubrowName(prev => ({ ...prev, [phaseId]: '' }));
    if (onDirtyChange) onDirtyChange(true);
  };

  // Toggle allocation for a resource/week
  const handleToggleAllocation = (phaseId: string, resourceId: string, weekIdx: number) => {
    if (!plan) return;
    const updatedPlan = {
      ...plan,
      phases: plan.phases.map(phase => {
        if (phase.id !== phaseId) return phase;
        return {
          ...phase,
          resources: phase.resources.map(resource => {
            if (resource.id !== resourceId) return resource;
            const existing = resource.allocations.find(a => a.week === weekIdx && a.allocated);
            let newAllocations;
            if (existing) {
              // Remove allocation
              newAllocations = resource.allocations.filter(a => !(a.week === weekIdx && a.allocated));
            } else {
              // Add allocation
              newAllocations = [...resource.allocations, { week: weekIdx, allocated: true }];
            }
            return { ...resource, allocations: newAllocations };
          })
        };
      })
    };
    if (!scenario) return;
    const currentScenarioId = scenario.id;
    setScenarios(scenarios.map(s =>
      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
    ));
    if (onDirtyChange) onDirtyChange(true);
  };

  // --- Gantt Chart Statistics ---
  // Memoize statistics so they always update when scenario or plan changes
  const {
    totalEstHours,
    totalPlannedHours,
    totalCosts,
    totalCommercials,
    estVsPlannedPct,
    marginPct
  } = useMemo(() => {
    let totalEstHours = 0;
    let totalPlannedHours = 0;
    let totalCosts = 0;
    let totalCommercials = 0;
    if (plan && scenario) {
      // Total estimated hours
      if (Array.isArray(scenario.estimateItems)) {
        totalEstHours = scenario.estimateItems.reduce((sum, item) => sum + ((item.hours || 0) * (item.multiplier || 1)), 0);
      }
      // Total planned hours, costs, commercials
      if (Array.isArray(plan.phases)) {
        plan.phases.forEach(phase => {
          if (Array.isArray(phase.resources)) {
            phase.resources.forEach(resource => {
              // Planned hours: each allocation with allocated=true is 40 hours
              if (Array.isArray(resource.allocations)) {
                totalPlannedHours += resource.allocations.filter(a => a.allocated).length * (40 * resource.fte);
                // Costs and commercials: sum for each allocation
                totalCosts += resource.allocations.filter(a => a.allocated).length * (resource.costRate * (40 * resource.fte) || 0);
                totalCommercials += resource.allocations.filter(a => a.allocated).length * (resource.commercialRate * (40 * resource.fte) || 0);
              }
            });
          }
        });
      }
    }
    // Estimate vs planned hours percentage
    const estVsPlannedPct = totalEstHours > 0 ? Math.round((totalPlannedHours / totalEstHours) * 100) : 0;
    // Margin % - using same calculation as ProjectPricing component
    const marginPct = totalCommercials > 0 ? ((totalCommercials - totalCosts) / totalCommercials) * 100 : 0;
    return {
      totalEstHours,
      totalPlannedHours,
      totalCosts,
      totalCommercials,
      estVsPlannedPct,
      marginPct
    };
  }, [plan, scenario]);

  React.useEffect(() => {
    const newFrom = plan?.fromDate ? utcIsoToLocalDateString(plan.fromDate) : formatDate(getMonday(new Date()));
    const newTo = plan?.toDate ? utcIsoToLocalDateString(plan.toDate) : formatDate(addWeeks(getMonday(new Date()), 9));
    setFromDate(newFrom);
    setToDate(newTo);
  }, [plan?.fromDate, plan?.toDate, scenarioId]);

  return (
    <Container fluid className="" style={{ width: '100%', maxWidth: '100%' }}>
      {/* Remove Resource Confirmation Dialog */}
      <PopupDialog
        open={removeDialog.open}
        title="Remove Resource"
        onClose={() => setRemoveDialog({ open: false })}
        actions={
          <>
            <Button variant="secondary" onClick={() => setRemoveDialog({ open: false })} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => {
              if (!plan || !removeDialog.phaseId || !removeDialog.resourceId) return;
              const updatedPlan = {
                ...plan,
                phases: plan.phases.map(p =>
                  p.id === removeDialog.phaseId
                    ? {
                        ...p,
                        resources: p.resources.filter(r => r.id !== removeDialog.resourceId)
                      }
                    : p
                )
              };
              if (!scenario) return;
              const currentScenarioId = scenario.id;
              setScenarios(scenarios.map(s =>
                s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
              ));
              setRemoveDialog({ open: false });
              if (onDirtyChange) onDirtyChange(true);
            }}>
              Remove
            </Button>
          </>
        }
      >
        <div>Are you sure you want to remove <b>{removeDialog.resourceName}</b> from this phase?</div>
      </PopupDialog>
      <Card className="border-0" style={{ borderRadius: 16, background: '#f9fafb' }}>
        <Card.Body style={{ padding: 0, margin: 0 }}>
          <div className="d-flex flex-column flex-md-row align-items-md-end mb-4" style={{ width: '100%' }}>
            <div className="d-flex flex-row flex-md-row align-items-end" style={{ gap: 16 }}>
              <div>
                <Form.Label className="fw-semibold text-secondary" style={{ fontSize: 15 }}>From</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={e => {
                    const newFromDate = e.target.value;
                    // Calculate week count between old from/to (inclusive)
                    const oldFrom = new Date(fromDate);
                    const oldTo = new Date(toDate);
                    // Always use Mondays for week calculation
                    const startMonday = getMonday(oldFrom);
                    const endMonday = getMonday(oldTo);
                    // Number of weeks = (difference in ms / week) + 1 (inclusive)
                    const weekMs = 7 * 24 * 60 * 60 * 1000;
                    const weekCount = Math.round((endMonday.getTime() - startMonday.getTime()) / weekMs) + 1;
                    // Set new from Monday
                    const newFromMonday = getMonday(new Date(newFromDate));
                    // Calculate new to date: newFromMonday + (weekCount - 1) weeks
                    const newTo = addWeeks(newFromMonday, weekCount - 1);
                    const newToDateStr = formatDate(newTo);
                    setFromDate(formatDate(newFromMonday));
                    setToDate(newToDateStr);
                    if (onDirtyChange) onDirtyChange(true);
                    if (!plan || !scenario) return;
                    const updatedPlan = { ...plan, fromDate: localDateStringToUtcIso(formatDate(newFromMonday)), toDate: localDateStringToUtcIso(newToDateStr) };
                    const currentScenarioId = scenario.id;
                    setScenarios(scenarios.map(s =>
                      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                    ));
                  }}
                  onBlur={e => {
                    const newFromDate = e.target.value;
                    if (!plan || !scenario) return;
                    const updatedPlan = { ...plan, fromDate: localDateStringToUtcIso(newFromDate) };
                    const currentScenarioId = scenario.id;
                    setScenarios(scenarios.map(s =>
                      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                    ));
                  }}
                  style={{ minWidth: 140, borderRadius: 8, border: `1.5px solid ${BORDER_COLOR}`, background: '#fff', fontWeight: 500 }}
                />
              </div>
              <div>
                <Form.Label className="fw-semibold text-secondary" style={{ fontSize: 15 }}>To</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={e => {
                    const newToDate = e.target.value;
                    setToDate(newToDate);
                    if (onDirtyChange) onDirtyChange(true);
                    if (!plan || !scenario) return;
                    const updatedPlan = { ...plan, toDate: localDateStringToUtcIso(newToDate) };
                    const currentScenarioId = scenario.id;
                    setScenarios(scenarios.map(s =>
                      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                    ));
                  }}
                  onBlur={e => {
                    const newToDate = e.target.value;
                    if (!plan || !scenario) return;
                    const updatedPlan = { ...plan, toDate: localDateStringToUtcIso(newToDate) };
                    const currentScenarioId = scenario.id;
                    setScenarios(scenarios.map(s =>
                      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                    ));
                  }}
                  style={{ minWidth: 140, borderRadius: 8, border: `1.5px solid ${BORDER_COLOR}`, background: '#fff', fontWeight: 500 }}
                />
              </div>
            </div>
            <div className="ms-md-auto mt-3 mt-md-0" style={{ display: 'flex', gap: 32, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Total Est. Hours</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#22223b' }}>{totalEstHours}</div>
              </div>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Total Planned Hours</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#22223b' }}>{totalPlannedHours}</div>
              </div>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Est. vs Planned %</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#22223b' }}>{estVsPlannedPct}%</div>
              </div>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Total Costs</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#22223b' }}>{totalCosts.toFixed(2)}</div>
              </div>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Total Commercials</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#22223b' }}>{totalCommercials.toFixed(2)}</div>
              </div>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Margin %</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#22223b' }}>{marginPct.toFixed(1)}%</div>
              </div>
            </div>
          </div>
          <div className="table-responsive" style={{ overflowX: 'auto', borderRadius: 12, border: `1.5px solid ${BORDER_COLOR}`, background: '#fff' }}>
            <Table
              size="sm"
              className="align-middle mb-0"
              style={{ minWidth: FIRST_COL_WIDTH + weekMondays.length * WEEK_COL_WIDTH, borderCollapse: 'separate', borderSpacing: 0 }}
            >
              <thead style={{ background: HEADER_BG, borderBottom: `2px solid ${BORDER_COLOR}` }}>
                <tr>
                  <th style={{
                    width: FIRST_COL_WIDTH,
                    minWidth: FIRST_COL_WIDTH,
                    maxWidth: FIRST_COL_WIDTH,
                    position: 'sticky',
                    left: 0,
                    background: HEADER_BG,
                    zIndex: 2,
                    border: 'none',
                    borderRight: '1px solid #d1d5db', // gray-300
                    fontWeight: 700,
                    fontSize: 16,
                    letterSpacing: 0.2,
                    color: '#22223b',
                    boxShadow: '2px 0 8px -4px #e5e7eb44',
                  }}>Phase / Resource</th>
                  <th style={{
                    width: FTE_COL_WIDTH,
                    minWidth: FTE_COL_WIDTH,
                    maxWidth: FTE_COL_WIDTH,
                    position: 'sticky',
                    left: FIRST_COL_WIDTH,
                    background: HEADER_BG,
                    zIndex: 2,
                    border: 'none',
                    borderRight: '1px solid #d1d5db',
                    fontWeight: 700,
                    fontSize: 16,
                    color: '#22223b',
                    boxShadow: '2px 0 8px -4px #e5e7eb44',
                    textAlign: 'center',
                  }}>FTE</th>
                  <th style={{
                    width: RATE_COL_WIDTH,
                    minWidth: RATE_COL_WIDTH,
                    maxWidth: RATE_COL_WIDTH,
                    position: 'sticky',
                    left: FIRST_COL_WIDTH + FTE_COL_WIDTH,
                    background: HEADER_BG,
                    zIndex: 2,
                    border: 'none',
                    borderRight: '1px solid #d1d5db',
                    fontWeight: 700,
                    fontSize: 16,
                    color: '#22223b',
                    boxShadow: '2px 0 8px -4px #e5e7eb44',
                    textAlign: 'center',
                  }}>Cost / Comm. Rate</th>
                  {weekMondays.map((monday, i) => (
                    <OverlayTrigger
                      key={i}
                      placement="top"
                      overlay={<Tooltip id={`tooltip-week-${i}`}>{formatDate(monday)}</Tooltip>}
                    >
                      <th
                        className="text-center"
                        style={{
                          minWidth: WEEK_COL_WIDTH,
                          maxWidth: WEEK_COL_WIDTH,
                          width: WEEK_COL_WIDTH,
                          fontWeight: 500,
                          fontSize: 15,
                          cursor: 'pointer',
                          background: HEADER_BG,
                          color: '#22223b',
                          border: 'none',
                          boxShadow: i === 0 ? '2px 0 8px -4px #e5e7eb44' : undefined
                        }}
                      >
                        {`Week ${i + 1}`}
                      </th>
                    </OverlayTrigger>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plan?.phases?.map(phase => {
                  // Calculate total estimated hours for the phase from scenario.estimateItems
                  let estHours = 0;
                  if (scenario && Array.isArray(scenario.estimateItems)) {
                    estHours = scenario.estimateItems
                      .filter(item => item.phaseId === phase.id)
                      .reduce((sum, item) => sum + ((item.hours || 0) * (item.multiplier || 1)), 0);
                  }

                  // Calculate total planned hours for the phase (from allocations)
                  let plannedHours = 0;
                  if (phase.resources && Array.isArray(phase.resources)) {
                    plannedHours = phase.resources.reduce((sum, resource) => {
                      if (!resource.allocations) return sum;
                      // Each allocation with allocated=true is 40 hours
                      const resourceHours = resource.allocations.filter(a => a.allocated).length * 40 * resource.fte;
                      return sum + resourceHours;
                    }, 0);
                  }

                  return (
                    <React.Fragment key={phase.id}>
                      {/* Phase header row */}
                      <tr style={{ background: PHASE_BG }}>
                        <td
                          className="fw-bold"
                          style={{
                            position: 'sticky',
                            left: 0,
                            background: PHASE_BG,
                            zIndex: 3,
                            minWidth: FIRST_COL_WIDTH,
                            maxWidth: FIRST_COL_WIDTH,
                            width: FIRST_COL_WIDTH,
                            border: 'none',
                            borderRight: '1px solid #d1d5db', // gray-300
                            fontWeight: 700,
                            fontSize: 15,
                            color: '#22223b',
                            letterSpacing: 0.1,
                            boxShadow: '2px 0 8px -4px #e5e7eb44',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>{phase.name}</span>
                            <span style={{ fontWeight: 400, fontSize: 13, color: '#6b7280', marginLeft: 10, marginRight:10, whiteSpace: 'nowrap' }}>
                              (Est: {estHours}hrs - Plan: {plannedHours}hrs)
                            </span>
                          </div>
                        </td>
                        <td style={{
                          width: FTE_COL_WIDTH,
                          minWidth: FTE_COL_WIDTH,
                          maxWidth: FTE_COL_WIDTH,
                          position: 'sticky',
                          left: FIRST_COL_WIDTH,
                          background: PHASE_BG,
                          zIndex: 3,
                          border: 'none',
                          borderRight: '1px solid #d1d5db',
                        }} />
                        <td style={{
                          width: RATE_COL_WIDTH,
                          minWidth: RATE_COL_WIDTH,
                          maxWidth: RATE_COL_WIDTH,
                          position: 'sticky',
                          left: FIRST_COL_WIDTH + FTE_COL_WIDTH,
                          background: PHASE_BG,
                          zIndex: 3,
                          border: 'none',
                          borderRight: '1px solid #d1d5db',
                        }} />
                        {weekMondays.map((_, i) => (
                          <td key={i} style={{ background: PHASE_BG, border: 'none' }} />
                        ))}
                      </tr>
                      {/* Resource rows */}
                      {phase.resources.map(resource => {
                        const cells = [];
                        for (let i = 0; i < weekMondays.length; i++) {
                          const isAllocated = resource.allocations.some(a => a.week === i && a.allocated);
                          cells.push(
                            <td
                              key={i}
                              style={{
                                padding: 0,
                                height: 36,
                                background: RESOURCE_BG,
                                cursor: 'pointer',
                                minWidth: WEEK_COL_WIDTH,
                                maxWidth: WEEK_COL_WIDTH,
                                width: WEEK_COL_WIDTH,
                                border: 'none',
                                borderBottom: `1.5px solid ${BORDER_COLOR}`,
                                transition: 'background 0.2s',
                                position: 'relative',
                              }}
                              onClick={() => handleToggleAllocation(phase.id, resource.id, i)}
                            >
                              {isAllocated && (
                                <div
                                  style={{
                                    height: 22,
                                    margin: 3,
                                    background: ALLOC_COLOR,
                                    borderRadius: 0,
                                    boxShadow: '0 2px 8px 0 #6366f122',
                                    width: '100%',
                                    cursor: 'pointer',
                                    border: `1.5px solid ${ALLOC_COLOR}`,
                                    transition: 'background 0.2s',
                                  }}
                                />
                              )}
                            </td>
                          );
                        }
                        // Handler to update FTE for this resource
                        const handleFteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                          let newFte = e.target.value;
                          // Only allow numbers with up to 1 decimal, and empty string
                          if (!/^\d*(\.\d{0,1})?$/.test(newFte)) return;
                          // Always store as number, default to 0 if empty
                          const parsedFte = newFte === '' ? 0 : Number(newFte);
                          const updatedPlan = {
                            ...plan,
                            phases: plan.phases.map(p =>
                              p.id === phase.id
                                ? {
                                    ...p,
                                    resources: p.resources.map(r =>
                                      r.id === resource.id
                                        ? { ...r, fte: parsedFte }
                                        : r
                                    )
                                  }
                                : p
                            )
                          };
                          if (!scenario) return;
                          const currentScenarioId = scenario.id;
                          setScenarios(scenarios.map(s =>
                            s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                          ));
                          if (onDirtyChange) onDirtyChange(true);
                        };
                        return (
                          <tr key={resource.id}>
                            <td className="fw-medium text-dark" style={{
                              paddingLeft: 32,
                              minWidth: FIRST_COL_WIDTH,
                              maxWidth: FIRST_COL_WIDTH,
                              width: FIRST_COL_WIDTH,
                              position: 'sticky',
                              left: 0,
                              background: RESOURCE_BG,
                              zIndex: 2,
                              border: 'none',
                              borderRight: '1px solid #d1d5db', // gray-300
                              borderBottom: `1.5px solid ${BORDER_COLOR}`,
                              fontWeight: 500,
                              fontSize: 15,
                              color: '#22223b',
                              
                            }}>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{ padding: '2px 6px', fontSize: 13, lineHeight: 1, borderRadius: 4, marginRight: 8 }}
                                title="Remove resource"
                                onClick={() => {
                                  setRemoveDialog({
                                    open: true,
                                    phaseId: phase.id,
                                    resourceId: resource.id,
                                    resourceName: resource.name
                                  });
                                }}
                              >
                                &times;
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ padding: '2px 6px', fontSize: 13, lineHeight: 1, borderRadius: 4 }}
                                title="Rename resource"
                                onClick={() => setEditingResource({ phaseId: phase.id, resourceId: resource.id, name: resource.name })}
                              >
                                <span role="img" aria-label="Edit">✎</span>
                              </Button>
                              {editingResource.phaseId === phase.id && editingResource.resourceId === resource.id ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingResource.name || ''}
                                    autoFocus
                                    style={{ marginLeft: 12, fontSize: 15, fontWeight: 500, color: '#22223b', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 6px', width: 200 }}
                                    onChange={e => setEditingResource({ ...editingResource, name: e.target.value })}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        // Confirm rename
                                        if (!plan) return;
                                        const updatedPlan = {
                                          ...plan,
                                          phases: plan.phases.map(p =>
                                            p.id === phase.id
                                              ? {
                                                  ...p,
                                                  resources: p.resources.map(r =>
                                                    r.id === resource.id ? { ...r, name: editingResource.name || '' } : r
                                                  )
                                                }
                                              : p
                                          )
                                        };
                                        if (!scenario) return;
                                        const currentScenarioId = scenario.id;
                                        setScenarios(scenarios.map(s =>
                                          s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                                        ));
                                        if (onDirtyChange) onDirtyChange(true);
                                        setEditingResource({});
                                      } else if (e.key === 'Escape') {
                                        setEditingResource({});
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="success"
                                    size="sm"
                                    style={{ marginLeft: 4, padding: '2px 6px', fontSize: 13, lineHeight: 1, borderRadius: 4 }}
                                    title="Confirm rename"
                                    onClick={() => {
                                      if (!plan) return;
                                      const updatedPlan = {
                                        ...plan,
                                        phases: plan.phases.map(p =>
                                          p.id === phase.id
                                            ? {
                                                ...p,
                                                resources: p.resources.map(r =>
                                                  r.id === resource.id ? { ...r, name: editingResource.name || '' } : r
                                                )
                                              }
                                            : p
                                        )
                                      };
                                      if (!scenario) return;
                                      const currentScenarioId = scenario.id;
                                      setScenarios(scenarios.map(s =>
                                        s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                                      ));
                                      if (onDirtyChange) onDirtyChange(true);
                                      setEditingResource({});
                                    }}
                                  >
                                    ✓
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    style={{ marginLeft: 2, padding: '2px 6px', fontSize: 13, lineHeight: 1, borderRadius: 4 }}
                                    title="Cancel rename"
                                    onClick={() => setEditingResource({})}
                                  >
                                    ✕
                                  </Button>
                                </>
                              ) : (
                                <span style={{ marginLeft:12 }}>{resource.name}</span>
                              )}
                            </td>
                            <td style={{
                              width: FTE_COL_WIDTH,
                              minWidth: FTE_COL_WIDTH,
                              maxWidth: FTE_COL_WIDTH,
                              position: 'sticky',
                              left: FIRST_COL_WIDTH,
                              background: RESOURCE_BG,
                              zIndex: 2,
                              border: 'none',
                              borderRight: '1px solid #d1d5db',
                              borderBottom: `1.5px solid ${BORDER_COLOR}`,
                              textAlign: 'center',
                              fontWeight: 500,
                              fontSize: 15,
                              color: '#22223b',
                            }}>
                              <Form.Control
                                type="number"
                                min={0}
                                step={0.1}
                                value={resource.fte === 0 ? '' : resource.fte}
                                onChange={handleFteChange}
                                style={{
                                  width: '100%',
                                  minWidth: 0,
                                  maxWidth: '100%',
                                  padding: '2px 4px',
                                  fontWeight: 500,
                                  fontSize: 15,
                                  color: '#22223b',
                                  background: RESOURCE_BG,
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 4,
                                  textAlign: 'center',
                                }}
                              />
                            </td>
                            <td style={{
                              width: RATE_COL_WIDTH,
                              minWidth: RATE_COL_WIDTH,
                              maxWidth: RATE_COL_WIDTH,
                              position: 'sticky',
                              left: FIRST_COL_WIDTH + FTE_COL_WIDTH,
                              background: RESOURCE_BG,
                              zIndex: 2,
                              border: 'none',
                              borderRight: '1px solid #d1d5db',
                              borderBottom: `1.5px solid ${BORDER_COLOR}`,
                              textAlign: 'center',
                            }}>
                              <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
                                <Form.Control
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={resource.costRate === 0 ? '' : resource.costRate}
                                  onChange={e => {
                                    const newCost = e.target.value === '' ? 0 : Number(e.target.value);
                                    const updatedPlan = {
                                      ...plan,
                                      phases: plan.phases.map(p =>
                                        p.id === phase.id
                                          ? {
                                              ...p,
                                              resources: p.resources.map(r =>
                                                r.id === resource.id
                                                  ? { ...r, costRate: newCost }
                                                  : r
                                              )
                                            }
                                          : p
                                      )
                                    };
                                    if (!scenario) return;
                                    const currentScenarioId = scenario.id;
                                    setScenarios(scenarios.map(s =>
                                      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                                    ));
                                    if (onDirtyChange) onDirtyChange(true);
                                  }}
                                  style={{
                                    width: '45%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    padding: '2px 4px',
                                    fontWeight: 500,
                                    fontSize: 15,
                                    color: '#22223b',
                                    background: RESOURCE_BG,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 4,
                                    textAlign: 'center',
                                  }}
                                  placeholder="Cost"
                                  onFocus={e => handleShowRates(e, 'cost', resource.id)}
                                  onBlur={e => {
                                    // Only hide if focus moves outside the cost/commercial cell
                                    if (!e.relatedTarget || !(e.relatedTarget as HTMLElement).closest('.rate-cell-' + resource.id)) {
                                      handleHideRates();
                                    }
                                  }}
                                />
                                <span style={{ fontWeight: 600, color: '#888' }}>/</span>
                                <Form.Control
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={resource.commercialRate === 0 ? '' : resource.commercialRate}
                                  onChange={e => {
                                    const newComm = e.target.value === '' ? 0 : Number(e.target.value);
                                    const updatedPlan = {
                                      ...plan,
                                      phases: plan.phases.map(p =>
                                        p.id === phase.id
                                          ? {
                                              ...p,
                                              resources: p.resources.map(r =>
                                                r.id === resource.id
                                                  ? { ...r, commercialRate: newComm }
                                                  : r
                                              )
                                            }
                                          : p
                                      )
                                    };
                                    if (!scenario) return;
                                    const currentScenarioId = scenario.id;
                                    setScenarios(scenarios.map(s =>
                                      s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                                    ));
                                    if (onDirtyChange) onDirtyChange(true);
                                  }}
                                  style={{
                                    width: '45%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    padding: '2px 4px',
                                    fontWeight: 500,
                                    fontSize: 15,
                                    color: '#22223b',
                                    background: RESOURCE_BG,
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 4,
                                    textAlign: 'center',
                                  }}
                                  placeholder="Comm."
                                  onFocus={e => handleShowRates(e, 'commercial', resource.id)}
                                  onBlur={e => {
                                    if (!e.relatedTarget || !(e.relatedTarget as HTMLElement).closest('.rate-cell-' + resource.id)) {
                                      handleHideRates();
                                    }
                                  }}
                                />
                                {/* Overlay for career level rates popup */}
                                <Overlay
                                  show={ratePopover.show && ratePopover.resourceId === resource.id}
                                  target={ratePopover.target}
                                  placement="bottom"
                                  containerPadding={10}
                                >
                                  <div style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 4px 16px #0001', padding: 8, minWidth: 260, zIndex: 9999 }}>
                                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Career Level Rates</div>
                                    {careerLevelRates.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>Loading...</div>}
                                    {careerLevelRates.map(rate => (
                                      <div key={rate.id} style={{ padding: '4px 0', cursor: 'pointer', fontSize: 14 }}
                                        onMouseDown={e => {
                                          e.preventDefault();
                                          // Set both costRate and commercialRate from the selected rate
                                          const updatedPlan = {
                                            ...plan,
                                            phases: plan.phases.map(p =>
                                              p.id === phase.id
                                                ? {
                                                    ...p,
                                                    resources: p.resources.map(r =>
                                                      r.id === resource.id
                                                        ? {
                                                            ...r,
                                                            costRate: rate.costRate,
                                                            commercialRate: rate.commercialRate
                                                          }
                                                        : r
                                                    )
                                                  }
                                                : p
                                            )
                                          };
                                          if (!scenario) return;
                                          const currentScenarioId = scenario.id;
                                          setScenarios(scenarios.map(s =>
                                            s.id === currentScenarioId ? { ...s, ganttChartPlan: updatedPlan } : s
                                          ));
                                          if (onDirtyChange) onDirtyChange(true);
                                          handleHideRates();
                                        }}
                                      >
                                        {rate.careerLevel} - (cost: {rate.costRate}, commercial: {rate.commercialRate})
                                      </div>
                                    ))}
                                  </div>
                                </Overlay>
                              </div>
                            </td>
                            {cells}
                          </tr>
                        );
                      })}
                      {/* Add resource row */}
                      <tr>
                        <td
                          style={{
                            paddingLeft: 32,
                            minWidth: FIRST_COL_WIDTH,
                            maxWidth: FIRST_COL_WIDTH,
                            width: FIRST_COL_WIDTH,
                            position: 'sticky',
                            left: 0,
                            background: ADDROW_BG,
                            zIndex: 2,
                            border: 'none',
                            borderRight: '1px solid #d1d5db', // gray-300
                            borderBottom: `1.5px solid ${BORDER_COLOR}`,
                          }}
                        >
                          <InputGroup size="sm" className="mb-2" style={{ maxWidth: 400, marginBottom: 0 }}>
                            <Form.Control
                              placeholder="Add resource..."
                              value={newSubrowName[phase.id] || ''}
                              onChange={e => setNewSubrowName(prev => ({ ...prev, [phase.id]: e.target.value }))}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleAddSubrow(phase.id);
                              }}
                              style={{ borderRadius: '4px 0px 0px 4px', border: `1.5px solid ${BORDER_COLOR}`, background: '#fff', fontWeight: 500 }}
                            /> 
                            <Button
                              variant="secondary"
                              style={{ borderRadius: '0px 4px 4px 0px', fontWeight: 600, letterSpacing: 0.2 }}
                              onClick={() => handleAddSubrow(phase.id)}
                              disabled={!(newSubrowName[phase.id] || '').trim()}
                            >
                              Add
                            </Button>
                          </InputGroup>
                        </td>
                        <td
                          style={{
                            width: FTE_COL_WIDTH,
                            minWidth: FTE_COL_WIDTH,
                            maxWidth: FTE_COL_WIDTH,
                            position: 'sticky',
                            left: FIRST_COL_WIDTH,
                            background: ADDROW_BG,
                            zIndex: 2,
                            border: 'none',
                            borderRight: '1px solid #d1d5db',
                            borderBottom: `1.5px solid ${BORDER_COLOR}`,
                          }}
                        />
                        <td style={{
                          width: RATE_COL_WIDTH,
                          minWidth: RATE_COL_WIDTH,
                          maxWidth: RATE_COL_WIDTH,
                          position: 'sticky',
                          left: FIRST_COL_WIDTH + FTE_COL_WIDTH,
                          background: ADDROW_BG,
                          zIndex: 2,
                          border: 'none',
                          borderRight: '1px solid #d1d5db',
                          borderBottom: `1.5px solid ${BORDER_COLOR}`,
                        }} />
                        {weekMondays.map((_, i) => (
                          <td
                            key={i}
                            style={{
                              background: ADDROW_BG,
                              border: 'none',
                              borderBottom: `1.5px solid ${BORDER_COLOR}`,
                            }}
                          />
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default GanttChartPlan;